import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  OnInit,
  effect,
  inject,
  viewChild,
} from '@angular/core';
import { RouterLink } from '@angular/router';
import { ClarityConsentService } from '../../services/clarity-consent.service';

/**
 * The cookie banner for Microsoft Clarity (ADO #270), on every page including the login page.
 * It shows only when a Clarity project is configured (production) and no choice is stored, or
 * when "Definições de cookies" reopens it. Accept and Reject have the same size and weight.
 */
@Component({
  selector: 'app-cookie-banner',
  imports: [RouterLink],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    @if (consent.bannerOpen()) {
      <section
        #panel
        class="cookie-banner"
        role="dialog"
        aria-modal="false"
        aria-labelledby="cookie-banner-title"
        aria-describedby="cookie-banner-text"
        tabindex="-1"
      >
        <h2 id="cookie-banner-title" class="cookie-banner__title">Cookies e Microsoft Clarity</h2>
        <div id="cookie-banner-text" class="cookie-banner__text">
          <p>
            Utilizamos cookies e o Microsoft Clarity para perceber como o site é usado e melhorá-lo. Se
            aceitar, o Clarity recolhe: os cliques, movimentos do rato e deslocamento na página; as páginas
            visitadas e o tempo em cada uma; a página de onde veio; o tipo de dispositivo, sistema operativo,
            navegador, idioma e resolução do ecrã; a localização aproximada (país/região) a partir do
            endereço IP; e uma gravação da sessão que reproduz a sua navegação. O texto que escreve em
            formulários e os dados pessoais são ocultados antes de serem enviados. Os dados são tratados pela
            Microsoft em nosso nome e guardados até 9 meses (a maior parte das gravações até 30 dias).
          </p>
          <p>
            <strong>Ao aceitar os cookies, aceita ser sujeito a esta recolha pelo Microsoft Clarity.</strong>
            Pode recusar sem qualquer impacto no uso do site e alterar a sua escolha a qualquer momento em
            "Definições de cookies".
          </p>
          @if (consent.choice(); as current) {
            <p class="cookie-banner__current">
              Escolha actual: {{ current === 'accepted' ? 'aceitou' : 'recusou' }}.
            </p>
          }
        </div>
        <div class="cookie-banner__actions">
          <button type="button" class="cookie-banner__btn" (click)="consent.accept()">Aceitar</button>
          <button type="button" class="cookie-banner__btn" (click)="consent.reject()">Recusar</button>
          <a class="cookie-banner__more" routerLink="/cookies">Saber mais</a>
        </div>
      </section>
    }
  `,
  styles: [
    `
      .cookie-banner {
        position: fixed;
        left: 16px;
        right: 16px;
        /* Above the mobile bottom bar (0 without one; styles.scss). */
        bottom: calc(16px + var(--bids-bottom-bar-height, 0px));
        z-index: 1100;
        max-width: 760px;
        max-height: calc(100dvh - 32px - var(--bids-bottom-bar-height, 0px));
        margin: 0 auto;
        overflow-y: auto;
        padding: 16px 20px;
        border-radius: 8px;
        background: #ffffff;
        color: #1b1d21;
        box-shadow: 0 6px 24px rgba(15, 23, 42, 0.25);
        font-size: 14px;
        line-height: 1.45;
      }
      .cookie-banner:focus {
        outline: 2px solid #1f5fa9;
        outline-offset: 2px;
      }
      .cookie-banner__title {
        margin: 0 0 8px;
        font-size: 16px;
        font-weight: 600;
      }
      .cookie-banner__text p {
        margin: 0 0 8px;
      }
      .cookie-banner__current {
        color: #475569;
      }
      .cookie-banner__actions {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 12px;
        margin-top: 12px;
      }
      .cookie-banner__btn {
        min-width: 120px;
        min-height: 44px;
        padding: 0 20px;
        border: 2px solid #1f5fa9;
        border-radius: 6px;
        background: #1f5fa9;
        color: #ffffff;
        font-size: 15px;
        font-weight: 600;
        cursor: pointer;
      }
      .cookie-banner__btn:focus-visible,
      .cookie-banner__more:focus-visible {
        outline: 2px solid #0f172a;
        outline-offset: 2px;
      }
      .cookie-banner__more {
        color: #1f5fa9;
        text-decoration: underline;
      }
      @media (max-width: 480px) {
        .cookie-banner__btn {
          flex: 1 1 0;
        }
        .cookie-banner__more {
          flex-basis: 100%;
        }
      }
    `,
  ],
})
export class CookieBannerComponent implements OnInit {
  protected readonly consent = inject(ClarityConsentService);
  private readonly panel = viewChild<ElementRef<HTMLElement>>('panel');

  constructor() {
    // Reopened from "Definições de cookies": move the focus to the banner for keyboard users.
    effect(() => {
      const el = this.panel();
      if (el && this.consent.reopened()) {
        el.nativeElement.focus();
      }
    });
  }

  ngOnInit(): void {
    this.consent.start();
  }
}
